import React from "react";
import { Link, useParams } from "wouter";
import { ArrowLeft, ArrowRight, Calendar, Clock, User } from "lucide-react";
import NotFound from "@/pages/NotFound";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { organizationSchema, personSchema } from "@/lib/seoConfig";
import { getBlogPost, getRecentPosts } from "@/lib/blogData";

function renderMarkdown(content: string) {
  // Simple markdown renderer for blog content
  const lines = content.split("\n");
  const elements: React.ReactElement[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("## ")) {
      elements.push(
        <h2
          key={i}
          className="text-2xl sm:text-3xl text-midnight mt-12 mb-6 font-['DM_Serif_Display',Georgia,serif]"
        >
          {line.replace("## ", "")}
        </h2>
      );
    } else if (line.startsWith("**") && line.endsWith("**")) {
      elements.push(
        <p key={i} className="text-midnight font-semibold mt-6 mb-2">
          {line.replace(/\*\*/g, "")}
        </p>
      );
    } else if (line.startsWith("**") && line.includes("**")) {
      // Bold text followed by regular text
      const boldMatch = line.match(/^\*\*(.+?)\*\*\s*(.*)/);
      if (boldMatch) {
        elements.push(
          <p key={i} className="text-slate-brand leading-relaxed mb-4">
            <strong className="text-midnight">{boldMatch[1]}</strong>{" "}
            {boldMatch[2]}
          </p>
        );
      }
    } else if (line.match(/^\d+\.\s\*\*/)) {
      // Numbered list with bold
      const match = line.match(/^(\d+)\.\s\*\*(.+?)\*\*\s*—?\s*(.*)/);
      if (match) {
        elements.push(
          <div key={i} className="flex gap-4 mb-4 pl-4">
            <span className="text-steel font-semibold flex-shrink-0">
              {match[1]}.
            </span>
            <p className="text-slate-brand leading-relaxed">
              <strong className="text-midnight">{match[2]}</strong>
              {match[3] ? ` — ${match[3]}` : ""}
            </p>
          </div>
        );
      }
    } else if (line.startsWith("- ")) {
      elements.push(
        <div key={i} className="flex gap-3 mb-2 pl-4">
          <span className="text-steel mt-2 flex-shrink-0">•</span>
          <p className="text-slate-brand leading-relaxed">
            {line.replace("- ", "")}
          </p>
        </div>
      );
    } else if (line.trim() === "") {
      // Skip empty lines
    } else {
      elements.push(
        <p key={i} className="text-slate-brand leading-relaxed mb-4">
          {line}
        </p>
      );
    }

    i++;
  }

  return elements;
}

export default function BlogPost() {
  const params = useParams<{ slug: string }>();
  const post = getBlogPost(params.slug || "");
  const recentPosts = getRecentPosts(3).filter(
    (p) => p.slug !== params.slug
  );

  if (!post) return <NotFound />;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `https://oc-international.com/insights/${post.slug}#article`,
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/hero-packaging-products-jTcsMjjFhH8thZMMjwdh3M.webp",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      "@id": "https://oc-international.com/about#chris-kenny",
      "@type": "Person",
      name: "Chris Kenny",
      jobTitle: "Managing Director",
      url: "https://oc-international.com/about",
    },
    publisher: {
      "@id": "https://oc-international.com/#organization",
      "@type": "Organization",
      name: "OC Recruitment",
      url: "https://oc-international.com",
      logo: {
        "@type": "ImageObject",
        url: "https://oc-international.com/manus-storage/oc-recruitment-logo-optimized_db13998d.webp",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://oc-international.com/insights/${post.slug}`,
    },
  };

  return (
    <div>
      <SEO
        title={post.metaTitle}
        description={post.metaDescription}
        keywords={post.keywords}
        canonical={`/insights/${post.slug}`}
        ogType="article"
        publishedTime={post.date}
        modifiedTime={post.date}
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [organizationSchema, personSchema, articleSchema],
        }}
      />

      {/* Article Header */}
      <section className="bg-midnight pt-32 pb-16">
        <div className="container">
          <div className="max-w-3xl">
            <ScrollReveal>
              <Link
                href="/insights"
                className="inline-flex items-center gap-2 text-white/50 text-sm hover:text-white transition-colors mb-8"
              >
                <ArrowLeft size={14} />
                Back to Insights
              </Link>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-xs font-semibold uppercase tracking-wider text-steel bg-white/10 px-3 py-1 rounded">
                  {post.category}
                </span>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.15] mb-6">
                {post.title}
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap items-center gap-6 text-sm text-white/50">
                <span className="flex items-center gap-2">
                  <User size={14} />
                  Chris Kenny
                </span>
                <span className="flex items-center gap-2">
                  <Calendar size={14} />
                  {new Date(post.date).toLocaleDateString("en-US", {
                    month: "long",
                    timeZone: "UTC",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
                <span className="flex items-center gap-2">
                  <Clock size={14} />
                  {post.readTime}
                </span>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container">
          <div className="max-w-3xl">
            <article className="prose-custom">
              {renderMarkdown(post.content)}
            </article>

            {/* Author Box */}
            <div className="mt-16 p-8 bg-pale rounded border border-border">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-full bg-midnight/10 flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-midnight/50" />
                </div>
                <div>
                  <h3 className="text-midnight font-semibold mb-1">
                    Chris Kenny
                  </h3>
                  <p className="text-steel text-sm mb-3">
                    Managing Director, OC Recruitment
                  </p>
                  <p className="text-slate-brand text-sm leading-relaxed">
                    Chris has over 16 years of experience in packaging sales
                    recruitment, working exclusively with US packaging
                    manufacturers, converters, and distributors to place
                    high-performing commercial talent.
                  </p>
                  <Link
                    href="/about"
                    className="inline-flex items-center gap-1 text-steel text-sm font-medium mt-3 hover:underline"
                  >
                    Learn more about Chris
                    <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 p-8 bg-midnight rounded text-center">
              <h3 className="text-xl text-white mb-3 font-['DM_Serif_Display',Georgia,serif]">
                Need Help With Your Packaging Sales Search?
              </h3>
              <p className="text-white/60 text-sm mb-6 max-w-lg mx-auto">
                Whether you are hiring commercial talent or exploring new
                opportunities in the packaging industry, Chris is available for
                a confidential conversation.
              </p>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-midnight text-sm font-semibold rounded hover:bg-white/90 transition-all group"
              >
                Get in Touch
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>
          </div>

          {/* Related Posts */}
          {recentPosts.length > 0 && (
            <div className="max-w-3xl mt-20">
              <h3 className="text-2xl text-midnight mb-8 font-['DM_Serif_Display',Georgia,serif]">
                More Insights
              </h3>
              <div className="grid sm:grid-cols-2 gap-6">
                {recentPosts.slice(0, 2).map((relatedPost) => (
                  <Link
                    key={relatedPost.slug}
                    href={`/insights/${relatedPost.slug}`}
                    className="group block p-6 rounded border border-border hover:border-steel/30 hover:shadow-md transition-all duration-300"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-steel">
                      {relatedPost.category}
                    </span>
                    <h4 className="text-midnight mt-2 mb-2 group-hover:text-steel transition-colors font-medium">
                      {relatedPost.title}
                    </h4>
                    <p className="text-slate-brand text-sm leading-relaxed line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
